import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Radio,
  TextField,
  Typography,
  createFilterOptions
} from "@mui/material";
import { cloneDeep } from "lodash";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import * as CommonStyles from "./CommonStyles";
import "./CommonStyles.scss";

interface Props {
  name: string;
  groupName: string;
  groupValues: any;
  title: string;
  description: string;
  options: any;
  totalOptions: number;
  values?: any;
  isRadioOption?: boolean;
  width?: number | string | null | undefined;
  onChange: any;
  onClose: (event: any, reason: string) => void;
  disabled?: boolean;
}

export default function GroupMultiSelectDropDown(props: Props) {
  const { t } = useTranslation();
  const disabled = props.disabled ?? false;
  const [expanded, setExpanded] = useState<string>("");
  const [expandedAll, setExpandedAll] = useState<boolean>(false);
  const [selectedOptionList, setSelectedOptionList] = useState<any>([]);
  const [itemsTodisplay, setItemsToDisplay] = useState<any>({
    showItemsCount: 3,
    expanded: false
  });

  const showMoreClick = () => {
    itemsTodisplay.showItemsCount === 3
      ? setItemsToDisplay({
          showItemsCount: selectedOptionList.length,
          expanded: true
        })
      : setItemsToDisplay({ showItemsCount: 3, expanded: false });
  };

  const filterOptions = createFilterOptions({
    stringify: (option: any) =>
      (option?.group?.label || "") + " " + (option?.label || "")
  });

  const getGroupLabel = (key: string) => {
    const groupData =
      props.options.find((option: any) => (option?.group?.key || "") === key) ||
      {};
    return groupData?.group?.label || "";
  };

  const getSearchOption = (value: string) => {
    setExpandedAll(value ? true : false);
  };

  const getSelectedOptions = () => {
    const copyFilterOptionList = cloneDeep(props.options);
    const selectedFilterList: any[] = [];
    const selectedGroupOptions: any[] = [];
    const selectedOptions =
      copyFilterOptionList.filter(
        (option: any) =>
          props.values[option.key] &&
          props.values[option.key] !== "Indeterminate"
      ) || [];
    if (props.groupValues && Object.keys(props.groupValues).length) {
      for (const key in props.groupValues) {
        selectedOptions
          .filter((option: any) => (option?.group?.key || "") === key)
          .map((option: any, index: number) => {
            if (index === 0 && option?.group) {
              option.group["isGroupTagDisplay"] = true;
              selectedGroupOptions.push(option.group || {});
            }
            return option;
          });
      }
    }
    if (selectedGroupOptions && selectedGroupOptions.length > 1) {
      selectedGroupOptions.sort(
        (a: any, b: any) => -(b?.label || "").localeCompare(a?.label || "")
      );
    }
    for (const groupOption of selectedGroupOptions) {
      selectedFilterList.push(groupOption);
      selectedOptions
        .filter((option: any) => (option?.group?.key || "") === groupOption.key)
        .map((option: any, index: number) => {
          selectedFilterList.push(option);
          return option;
        });
    }
    return selectedFilterList;
  };

  useEffect(() => {
    const selectedOptionsList = getSelectedOptions();
    if (itemsTodisplay.expanded) {
      const updateItemsToDisplay =
        selectedOptionsList.length > 3
          ? { showItemsCount: selectedOptionsList.length, expanded: true }
          : { showItemsCount: 3, expanded: false };
      setItemsToDisplay(updateItemsToDisplay);
    }
    setSelectedOptionList(selectedOptionsList);
  }, [props.values, props.options]);

  return (
    <Box className="filter-section">
      <h4 style={CommonStyles.titleElement}>{t(props.title)}</h4>
      <Typography sx={CommonStyles.descriptionElement}>
        {t(props.description)}
      </Typography>
      <Autocomplete
        multiple={!props.isRadioOption}
        fullWidth
        size="small"
        disabled={disabled}
        sx={{ width: props.width }}
        options={props.options}
        groupBy={(option: any) => option.group?.key || ""}
        getOptionLabel={(option: any) => option.label}
        renderTags={() => {
          return null;
        }}
        filterOptions={filterOptions}
        renderOption={(propsHTML, option) => (
          <li
            {...propsHTML}
            key={option.key + option.label}
            onClick={(e: any) => {
              if (!disabled)
                props.onChange(option.key, null, "DropDown", option?.group?.key);
            }}
            style={{
              paddingLeft: option.label !== "All" ? 32 : 16
            }}
          >
            {props.isRadioOption && (
              <Radio
                name={props.name}
                style={{ marginRight: 4 }}
                disabled={disabled}
                checked={props.values[option.key] ? true : false}
              />
            )}
            {!props.isRadioOption && (
              <Checkbox
                name={props.name}
                style={{ marginRight: 4 }}
                color="secondary"
                disabled={disabled}
                checked={props.values[option.key] ? true : false}
                indeterminate={props.values[option.key] === "Indeterminate"}
                sx={{
                  pointerEvents: "auto",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                    color: "var( --primary-color)"
                  }
                }}
              />
            )}
            {option.label}
          </li>
        )}
        renderGroup={(params) => (
          <Fragment key={params.key}>
            {!params.group && params.children}
            {params.group && (
              <Accordion
                key={params.group}
                expanded={expandedAll || expanded === params.group}
                sx={{
                  padding: 0,
                  "&.Mui-expanded": { margin: "0px !important" },
                  "&:before": { content: "none" }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      onClick={(e) =>
                        setExpanded(
                          expanded === params.group ? "" : params.group
                        )
                      }
                    />
                  }
                  aria-controls={"panel-content" + params.key}
                  id={"panel-header" + params.key}
                  sx={{
                    "&.Mui-expanded": {
                      minHeight: "54px !important",
                      margin: "0px !important"
                    },
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)"
                    },
                    ".MuiAccordionSummary-content": {
                      margin: 0,
                      alignItems: "center",

                      "&.Mui-expanded": {
                        margin: "0px !important"
                      }
                    }
                  }}
                >
                  {!props.isRadioOption && (
                    <Checkbox
                      name={props.name}
                      indeterminate={
                        props.groupValues[params.group] === "Indeterminate"
                      }
                      style={{ marginRight: 4 }}
                      color="secondary"
                      disabled={disabled}
                      checked={props.groupValues[params.group] ? true : false}
                      onChange={(e) => {
                        if (!disabled)
                          props.onChange(params.group, "All", "DropDown");
                        e.stopPropagation();
                      }}
                      sx={{
                        pointerEvents: "auto",
                        "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                          color: "var( --primary-color)"
                        }
                      }}
                    />
                  )}
                  <span
                    onClick={(e) =>
                      setExpanded(expanded === params.group ? "" : params.group)
                    }
                  >
                    {expandedAll
                      ? getGroupLabel(params.group)
                      : props.options[params.key]?.group?.label || ""}
                  </span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "0px !important" }}>
                  {params.children}
                </AccordionDetails>
              </Accordion>
            )}
          </Fragment>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              props.totalOptions === selectedOptionList.length
                ? t("pa.allSelected")
                : selectedOptionList.length > 1
                  ? t("pa.selectMore")
                  : t("pa.selectAnOption")
            }
            onChange={(e) => getSearchOption(e.target.value)}
          />
        )}
        onClose={(event: any, reason: string) => {
          setExpandedAll(false);
          setExpanded("");
          props.onClose(event, reason);
        }}
      />
      <Box className="selected-chips-wrapper">
        {!props.isRadioOption &&
          selectedOptionList.length > 0 &&
          selectedOptionList
            .slice(0, itemsTodisplay.showItemsCount)
            .map((item: any, index: number) => {
              return (
                <Fragment key={index}>
                  {item?.isGroupTagDisplay && (
                    <Chip
                      key={`chip-${props.groupName}-${item?.key}`}
                      label={t("pa.function") + item?.label}
                      style={{ marginBottom: 8 }}
                      deleteIcon={disabled ? undefined : <CloseIcon />}
                      onDelete={
                        disabled
                          ? undefined
                          : () => {
                              props.onChange(item?.key, "All", "Tag");
                            }
                      }
                    />
                  )}
                  {!item?.isGroupTagDisplay && (
                    <Chip
                      key={`chip-${props.name}-${item.key}`}
                      label={t("pa.subFunction") + item.label}
                      style={{ marginBottom: 8 }}
                      deleteIcon={disabled ? undefined : <CloseIcon />}
                      onDelete={
                        disabled
                          ? undefined
                          : () => {
                              props.onChange(
                                item.key,
                                null,
                                "Tag",
                                item?.group.key
                              );
                            }
                      }
                    />
                  )}
                </Fragment>
              );
            })}
        <span className="viewMore">
          {selectedOptionList.length > 3 && (
            <a
              href="#"
              onClick={() => {
                showMoreClick();
              }}
            >
              {itemsTodisplay.expanded ? (
                <span>{t("pa.viewLess")}</span>
              ) : (
                <span>
                  +{selectedOptionList.length - 3} {t("pa.more")}
                </span>
              )}
            </a>
          )}
          {selectedOptionList.length > 0 &&
            !props.isRadioOption &&
            !disabled && (
              <a
                href="#"
                onClick={() => {
                  props.onChange("", "CLEAR_ALL") &&
                    setItemsToDisplay({ showItemsCount: 3, expanded: false });
                }}
              >
                {t("pa.clearAllFilters")}
              </a>
            )}
        </span>
      </Box>
    </Box>
  );
}
